import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormik, FormikProvider } from 'formik'
import moment from 'moment'

import { PayrollSetupList } from './PayrollSetupList'
import { User } from '@/reducers/auth/userReducer'
import { getCurrentUser } from '@/selectors/user.selectors'
import { selectFirstEmployee } from '../payroll.selectors'
import {
  postCreateEmployee,
  fetchAllCompanyLocations,
  fetchEmployees,
  putUpdateEmployee,
  POST_CREATE_EMPLOYEE_KEY,
  PUT_UPDATE_EMPLOYEE_KEY,
} from '../payrollActions'
import { PrefilledNoticeContainer } from './PrefilledNoticeContainer'
import {
  GEP_ENROLL_PATHS,
  getMinDateOfBirth,
  ONBOARDING_STEPS,
  useUpdatePayrollOnboardingStep,
} from '../helpers'
import { PayrollError } from '../PayrollError'
import { Button, Card, Form, Separator, Text } from '@/ui'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useAnalyticsTrack } from '@/features/amplitude'
import { TypographyStyles } from '@/styles'

// TODO: Migrate to V2
import {
  getFieldNames,
  makeDateSchema,
  makeNumberSchema,
  makeReqEmailSchema,
  makeReqStringSchema,
} from '@/base'

const EmployeeForm = ({ user }: { user: User }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const track = useAnalyticsTrack()

  const maxDateOfBirth = getMinDateOfBirth()

  const employee = useReselector(selectFirstEmployee)

  const prefilledDob = useMemo(() => {
    if (employee?.date_of_birth) {
      return moment(employee.date_of_birth, DATE_FORMATS.GUSTO_SUBMIT).format(
        DATE_FORMATS.INPUT
      )
    } else if (user.dateOfBirth) {
      return moment(user.dateOfBirth).format(DATE_FORMATS.INPUT)
    }

    return ''
  }, [employee?.date_of_birth, user.dateOfBirth])

  const formik = useFormik({
    initialValues: {
      first_name: user.firstName || '',
      last_name: user.lastName || '',
      dob: prefilledDob,
      email: employee?.email || user.personalEmail || user.email,
      ssn: '',
    },
    onSubmit: async ({ first_name, last_name, dob, email, ssn }) => {
      // create/update employee
      const res = employee?.uuid
        ? await putUpdateEmployee(employee.uuid, {
            version: employee.version,
            first_name,
            last_name,
            date_of_birth: moment(dob, DATE_FORMATS.INPUT).format(
              DATE_FORMATS.GUSTO_SUBMIT
            ),
            email,
            ssn,
          })(dispatch)
        : await postCreateEmployee({
            first_name,
            last_name,
            date_of_birth: moment(dob, DATE_FORMATS.INPUT).format(
              DATE_FORMATS.GUSTO_SUBMIT
            ),
            email,
            ssn,
          })(dispatch)

      if (res) {
        track('completed payroll step - employee creation or update')
        navigate(GEP_ENROLL_PATHS.employeeJob)
      }
    },
  })

  const { submitForm, isSubmitting } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <div className="flex flex-col gap-4">
        <Text variant={TypographyStyles.H1} className="mt-4">
          Add yourself as an employee
        </Text>
        <Separator />

        <Text variant={TypographyStyles.BODY_MD}>Employment Details</Text>
        <Text variant={TypographyStyles.BODY_SM}>
          In order to pay yourself as an employee, you need to set yourself up
          as an employee.
        </Text>

        <PayrollError fetchKey={POST_CREATE_EMPLOYEE_KEY} />
        <PayrollError fetchKey={PUT_UPDATE_EMPLOYEE_KEY} />

        <div className="flex flex-wrap gap-6">
          <div className="w-full md:w-[48%]">
            <Form.Input
              name={fieldNames.first_name}
              label="First Name"
              required
              schema={makeReqStringSchema({ field: 'first name' })}
            />
          </div>
          <div className="w-full md:w-[48%]">
            <Form.Input
              name={fieldNames.last_name}
              label="Last Name"
              required
              schema={makeReqStringSchema({ field: 'last name' })}
            />
          </div>
          <div className="w-full md:w-[48%]">
            <Form.DateInput
              name={fieldNames.dob}
              maxDate={maxDateOfBirth.toJSDate()}
              required
              label="Date of Birth"
              schema={makeDateSchema({
                field: 'date of birth',
                maxDate: maxDateOfBirth.toFormat(DATE_FORMATS_LUXON.INPUT),
              })}
            />
          </div>
          <div className="w-full">
            <Form.Input
              name={fieldNames.email}
              label="Personal Email"
              description="We’ll send information regarding your payroll and personal pay to your email address."
              required
              schema={makeReqEmailSchema({ field: 'email' })}
            />
          </div>
          <div className="w-full">
            <Form.Input
              name={fieldNames.ssn}
              // TODO: Fix this
              // type={ssnType}
              type="text"
              placeholder="xxx-xx-xxxx"
              label="Social Security number"
              description={
                <>
                  We need this to enroll you in payroll.{' '}
                  <b>Heard doesn’t store this data.</b>
                </>
              }
              // onRightIconClick={() =>
              //   setSsnType((type) => (type === 'password' ? 'text' : 'password'))
              // }
              // rightIcon={ssnType === 'password' ? 'eye' : 'eye slash'}
              required
              schema={makeNumberSchema({
                field: 'social security number',
                numDigits: 9,
                allowLeadingZero: true,
              })}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mt-6">
          <div className="w-full md:w-1/2">
            <Button
              variant="secondary"
              onClick={() => navigate(GEP_ENROLL_PATHS.locations)}
            >
              Back
            </Button>
          </div>
          <div className="w-full md:w-1/2">
            <Button onClick={submitForm} loading={isSubmitting}>
              Save & Continue
            </Button>
          </div>
        </div>
      </div>
    </FormikProvider>
  )
}

export const PayrollEnrollEmployee = () => {
  const dispatch = useAppDispatch()
  const user = useReselector(getCurrentUser)
  const [fetched, setFetched] = useState(false)
  useUpdatePayrollOnboardingStep(ONBOARDING_STEPS.employeeCreation)

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchAllCompanyLocations())
      await dispatch(fetchEmployees())
      setFetched(true)
    }
    fetch()
  }, [dispatch])

  return (
    <Card>
      <Text variant={TypographyStyles.DISPLAY_XS}>Setting Up Payroll</Text>
      <Separator />
      <PrefilledNoticeContainer />

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="w-full lg:w-1/2">
          {user && fetched && <EmployeeForm user={user} />}
        </div>
        <div className="w-full lg:w-1/2">
          <PayrollSetupList />
        </div>
      </div>
    </Card>
  )
}
